import type Feature from "ol/Feature";
import type { Geometry } from "ol/geom";
import { LineString, Point } from "ol/geom";
import type { ServerControlPoint } from "./api-hiking";
import { inside4326Extent } from "./proj";
import { findOptimalVertexIndex } from "./utils";

/*
 * Get line coordinates from kml features
 */
export function getKmlCoordinates(features: Feature<Geometry>[]): number[][] {
  const lineFeatures = features.filter((feature) => {
    return feature.getGeometry() instanceof LineString;
  });

  const linesCoordinates = [];
  for (const lineFeature of lineFeatures) {
    linesCoordinates.push((<LineString>lineFeature.getGeometry()).getCoordinates());
  }

  if (lineFeatures.length === 0 || linesCoordinates.length === 0) {
    throw new Error("Could not find any tracks in the kml file.");
  }

  for (const point of linesCoordinates.flat()) {
    if (!inside4326Extent([point[0], point[1]])) {
      throw new Error("Coordinates must be inside swiss extent (EPSG 4326)");
    }
  }

  return linesCoordinates.flat();
}

export function getKmlControlPoints(features: Feature<Geometry>[], coordinates: number[][]): ServerControlPoint[] {
  const controlPointsWithStopovers: ServerControlPoint[] = [];

  const pointFeatures = features.filter((feature) => {
    return feature.getGeometry() instanceof Point;
  });

  const pointsCoordinates = [];
  for (const lineFeature of pointFeatures) {
    pointsCoordinates.push((<Point>lineFeature.getGeometry()).getCoordinates());
  }

  // Subarray of coordinates from the last control point found
  let remainingCoordinates = coordinates;
  let remainingWindowIndex = 0;

  for (const cp of pointsCoordinates) {
    const optimal_vertex_index = remainingWindowIndex + findOptimalVertexIndex(remainingCoordinates, [cp[0], cp[1]]);

    // Since we later use these indexes to break line into segments, the condition avoids creating segments with only one point
    // in the cases where the optimal vertex is just one index ahead of the previous one
    remainingWindowIndex = optimal_vertex_index + (optimal_vertex_index === remainingWindowIndex ? 2 : 1);

    remainingCoordinates = coordinates.slice(remainingWindowIndex, coordinates.length);
    controlPointsWithStopovers.push({
      vertex_index: optimal_vertex_index,
      // snaps to line
      coordinates: coordinates[optimal_vertex_index],
      name: null,
      features: [],
      distance: Number.NaN,
      duration: Number.NaN,
    });
  }

  // Create start and end control points, if they are not already in the list
  if (!controlPointsWithStopovers[0] || controlPointsWithStopovers[0].vertex_index !== 0) {
    controlPointsWithStopovers.unshift({
      vertex_index: 0,
      coordinates: coordinates[0],
      name: "Start point",
      features: [],
      distance: Number.NaN,
      duration: Number.NaN,
    });
  }

  if (
    controlPointsWithStopovers.at(-1)?.vertex_index !== coordinates.length - 1 ||
    controlPointsWithStopovers.length === 1
  ) {
    controlPointsWithStopovers.push({
      vertex_index: coordinates.length - 1,
      coordinates: coordinates[coordinates.length - 1],
      name: "End point",
      features: [],
      distance: Number.NaN,
      duration: Number.NaN,
    });
  }

  return controlPointsWithStopovers;
}
